import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  addResearchWorkItemNote,
  IAddResearchWorkItemNoteRequest,
} from "api/addResearchWorkItemNote";
import {
  assignResearchWorkItem,
  IResearchAssignmentRequest,
} from "api/assignResearchWorkItem";
import {
  cancelResearchDemand,
  ICancelResearchDemandRequest,
  ICancelResearchDemandResponse,
} from "api/cancelResearchDemand";
import { claimResearchWorkItem } from "api/claimResearchWorkItem";
import { createResearchIntake } from "api/createResearchIntake";
import {
  getResearchProductivity,
  IResearchProductivityRange,
} from "api/getResearchProductivity";
import {
  getResearchClientProgress,
  IResearchClientProgressFilters,
} from "api/getResearchClientProgress";
import { getResearchPropertyHistory } from "api/getResearchPropertyHistory";
import { getResearchReferenceData } from "api/getResearchReferenceData";
import { getResearchSummary } from "api/getResearchSummary";
import { getResearchWorkItemPath } from "api/getResearchWorkItemPath";
import { getResearchWorkItems } from "api/getResearchWorkItems";
import { previewResearchIntake } from "api/previewResearchIntake";
import {
  requireFixResearchComp,
  IRequireFixResearchCompRequest,
} from "api/requireFixResearchComp";
import {
  updateResearchDemandMetadata,
  IResearchDemandMetadataRequest,
} from "api/updateResearchDemandMetadata";
import {
  transitionResearchWorkItem,
  IResearchTransitionRequest,
} from "api/transitionResearchWorkItem";
import {
  IResearchUnclaimRequest,
  unclaimResearchWorkItem,
} from "api/unclaimResearchWorkItem";
import { toastMessage } from "components/ToastMessage";
import { TServiceError } from "types/general";
import { RESEARCH_SLICE_NAME } from "./constants";
import {
  IResearchIntakePreview,
  IResearchIntakeRequest,
  IResearchIntakeResponse,
  IResearchClientProgress,
  IResearchProductivity,
  IResearchPropertyPath,
  IResearchReferenceData,
  IResearchSummary,
  IResearchSummaryFilters,
  IResearchWorkFilters,
  IResearchWorkItem,
} from "./types";

type TResearchThunkConfig = { rejectValue: TServiceError };

interface IResearchRequestError {
  response?: {
    status?: number;
    data?: unknown;
  };
  message?: string;
}

const INVALID_INPUT_PREFIX = "Invalid input: [";
const INVALID_ACTION_PREFIX =
  "The user is not permitted to perform an action: [";

const getResponseMessage = (error: unknown): string | undefined => {
  if (typeof error !== "object" || error === null) return undefined;

  const responseMessage = (error as IResearchRequestError).response?.data;

  return typeof responseMessage === "string" && responseMessage.trim()
    ? responseMessage.trim()
    : undefined;
};

const formatResponseMessage = (message: string): string => {
  const wrappedPrefixes = [INVALID_INPUT_PREFIX, INVALID_ACTION_PREFIX];
  const wrappedPrefix = wrappedPrefixes.find(
    (prefix) => message.startsWith(prefix) && message.endsWith("]")
  );
  const unwrappedMessage = wrappedPrefix
    ? message.slice(wrappedPrefix.length, -1).trim()
    : message;

  return /[.!?]$/.test(unwrappedMessage)
    ? unwrappedMessage
    : `${unwrappedMessage}.`;
};

const getError = (error: unknown): TServiceError => {
  const requestError =
    typeof error === "object" && error !== null
      ? (error as IResearchRequestError)
      : {};
  const responseMessage = getResponseMessage(error);

  return {
    code: requestError.response?.status ?? 0,
    message:
      responseMessage ??
      requestError.message ??
      "An unexpected WIP request error occurred.",
  };
};

const showError = (message: string) => {
  toastMessage({ message, type: "error", position: "top-right" });
};

const showRequestError = (
  error: unknown,
  fallbackMessage: string
): TServiceError => {
  const requestError = getError(error);
  const responseMessage = getResponseMessage(error);
  const isExpectedClientError =
    requestError.code >= 400 &&
    requestError.code < 500 &&
    requestError.code !== 401;

  showError(
    isExpectedClientError && responseMessage
      ? formatResponseMessage(responseMessage)
      : fallbackMessage
  );

  return requestError;
};

export const fetchResearchReferenceData = createAsyncThunk<
  IResearchReferenceData,
  void,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchReferenceData`,
  async (_, { rejectWithValue }) => {
    try {
      return await getResearchReferenceData();
    } catch (error) {
      showError("Failed to load WIP reference data. Please try again.");
      return rejectWithValue(getError(error));
    }
  }
);

export const fetchResearchSummary = createAsyncThunk<
  IResearchSummary,
  IResearchSummaryFilters | undefined,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchSummary`,
  async (filters, { rejectWithValue }) => {
    try {
      return await getResearchSummary(filters);
    } catch (error) {
      showError("Failed to load the WIP summary. Please try again.");
      return rejectWithValue(getError(error));
    }
  }
);

export const fetchResearchWorkItems = createAsyncThunk<
  IResearchWorkItem[],
  IResearchWorkFilters | undefined,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchWorkItems`,
  async (filters, { rejectWithValue }) => {
    try {
      return await getResearchWorkItems(filters);
    } catch (error) {
      showError("Failed to load WIP items. Please try again.");
      return rejectWithValue(getError(error));
    }
  }
);

export const fetchResearchClientProgress = createAsyncThunk<
  IResearchClientProgress[],
  IResearchClientProgressFilters,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchClientProgress`,
  async (filters, { rejectWithValue }) => {
    try {
      return await getResearchClientProgress(filters);
    } catch (error) {
      showError("Failed to load client WIP progress. Please try again.");
      return rejectWithValue(getError(error));
    }
  }
);

export const fetchResearchWorkItemPath = createAsyncThunk<
  IResearchPropertyPath,
  string,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchWorkItemPath`,
  async (workItemId, { rejectWithValue }) => {
    try {
      return await getResearchWorkItemPath(workItemId);
    } catch (error) {
      showError("Failed to load this Property's WIP path.");
      return rejectWithValue(getError(error));
    }
  }
);

export const fetchResearchPropertyHistory = createAsyncThunk<
  IResearchPropertyPath[],
  string,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchPropertyHistory`,
  async (propertyId, { rejectWithValue }) => {
    try {
      return await getResearchPropertyHistory(propertyId);
    } catch (error) {
      showError("Failed to load this Property's WIP history.");
      return rejectWithValue(getError(error));
    }
  }
);

export const fetchResearchProductivity = createAsyncThunk<
  IResearchProductivity,
  IResearchProductivityRange,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/fetchProductivity`,
  async (range, { rejectWithValue }) => {
    try {
      return await getResearchProductivity(range);
    } catch (error) {
      showError("Failed to load Research Team productivity.");
      return rejectWithValue(getError(error));
    }
  }
);

export const previewIntake = createAsyncThunk<
  IResearchIntakePreview,
  IResearchIntakeRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/previewIntake`,
  async (request, { rejectWithValue }) => {
    try {
      return await previewResearchIntake(request);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "The Add to WIP preview could not be created. Please try again."
        )
      );
    }
  }
);

export const submitIntake = createAsyncThunk<
  IResearchIntakeResponse,
  IResearchIntakeRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/submitIntake`,
  async (request, { rejectWithValue }) => {
    try {
      const response = await createResearchIntake(request);
      toastMessage({
        message: "The selected Properties were processed and added to WIP.",
        type: "success",
        position: "top-right",
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "The selected Properties could not be added to WIP. Please try again."
        )
      );
    }
  }
);

export const claimWorkItem = createAsyncThunk<
  IResearchWorkItem,
  { workItemId: string; version: number },
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/claimWorkItem`,
  async ({ workItemId, version }, { rejectWithValue }) => {
    try {
      return await claimResearchWorkItem(workItemId, version);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "This WIP item could not be claimed. Refresh and try again."
        )
      );
    }
  }
);

export const unclaimWorkItem = createAsyncThunk<
  IResearchWorkItem,
  IResearchUnclaimRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/unclaimWorkItem`,
  async (request, { rejectWithValue }) => {
    try {
      return await unclaimResearchWorkItem(request);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "This WIP phase could not be unclaimed. Refresh and try again."
        )
      );
    }
  }
);

export const transitionWorkItem = createAsyncThunk<
  IResearchWorkItem,
  IResearchTransitionRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/transitionWorkItem`,
  async (request, { rejectWithValue }) => {
    try {
      return await transitionResearchWorkItem(request);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "The WIP stage could not be changed. Refresh and try again."
        )
      );
    }
  }
);

export const addWorkItemNote = createAsyncThunk<
  IResearchWorkItem,
  IAddResearchWorkItemNoteRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/addWorkItemNote`,
  async (request, { rejectWithValue }) => {
    try {
      return await addResearchWorkItemNote(request);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "The WIP note could not be saved. Refresh and try again."
        )
      );
    }
  }
);

export const assignWorkItem = createAsyncThunk<
  IResearchWorkItem,
  IResearchAssignmentRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/assignWorkItem`,
  async (request, { rejectWithValue }) => {
    try {
      return await assignResearchWorkItem(request);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "The WIP assignment could not be changed. Please try again."
        )
      );
    }
  }
);

export const cancelDemand = createAsyncThunk<
  ICancelResearchDemandResponse,
  ICancelResearchDemandRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/cancelDemand`,
  async (request, { rejectWithValue }) => {
    try {
      return await cancelResearchDemand(request);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "The client request could not be cancelled. Please try again."
        )
      );
    }
  }
);

export const requireFix = createAsyncThunk<
  void,
  IRequireFixResearchCompRequest,
  TResearchThunkConfig
>(`${RESEARCH_SLICE_NAME}/requireFix`, async (request, { rejectWithValue }) => {
  try {
    await requireFixResearchComp(request);
    toastMessage({
      message: "The Comp was returned to the researcher with review notes.",
      type: "success",
      position: "top-right",
    });
    return;
  } catch (error) {
    return rejectWithValue(
      showRequestError(
        error,
        "The Comp could not be returned for fixes. Please try again."
      )
    );
  }
});

export const updateDemandMetadata = createAsyncThunk<
  IResearchWorkItem,
  IResearchDemandMetadataRequest,
  TResearchThunkConfig
>(
  `${RESEARCH_SLICE_NAME}/updateDemandMetadata`,
  async (request, { rejectWithValue }) => {
    try {
      return await updateResearchDemandMetadata(request);
    } catch (error) {
      return rejectWithValue(
        showRequestError(
          error,
          "The client request could not be updated. Please try again."
        )
      );
    }
  }
);
